import { Suspense } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../../components/SEOHead';
import Breadcrumb from '../../../components/Breadcrumb';

export default function GreenBayPage() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Technelle AI Marketing - Green Bay',
    description:
      'AI-driven marketing agency serving Green Bay businesses with advanced automation, lead generation, and growth strategies tailored to Northeast Wisconsin.',
    url: 'https://technelle.ai/service-areas/green-bay',
    telephone: '414-530-9114',
    email: 'melissa@technelle.ai',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '2810 Crossroads Dr Suite 4000',
      addressLocality: 'Waukesha',
      addressRegion: 'WI',
      postalCode: '53189',
      addressCountry: 'US',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '44.5133',
      longitude: '-88.0133',
    },
    areaServed: {
      '@type': 'City',
      name: 'Green Bay',
      '@id': 'https://en.wikipedia.org/wiki/Green_Bay,_Wisconsin',
    },
    priceRange: '$$',
    openingHours: 'Mo-Fr 09:00-17:00',
  };

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <SEOHead
        title="AI Marketing Agency Green Bay WI | Technelle | Northeast Wisconsin"
        description="AI marketing and automation for Green Bay businesses. Technelle helps Green Bay manufacturers, hospitality companies, and B2B firms generate more leads and grow revenue. 20+ years Wisconsin experience."
        keywords="AI marketing Green Bay WI, marketing agency Green Bay Wisconsin, AI automation Green Bay, lead generation Green Bay, Northeast Wisconsin marketing, Green Bay business growth"
        canonical="https://technelle.ai/service-areas/green-bay"
        ogImage="https://public.readdy.ai/ai/img_res/02ba79a7677e8c7de84e52056d4674de.jpg"
        structuredData={structuredData}
        geoPosition="44.5133;-88.0133"
        geoRegion="US-WI"
        geoPlacename="Green Bay"
        breadcrumbs={[
          { name: 'Home', url: 'https://technelle.ai/' },
          { name: 'Service Areas', url: 'https://technelle.ai/service-areas' },
          { name: 'Green Bay', url: 'https://technelle.ai/service-areas/green-bay' },
        ]}
      />

      <div className="min-h-screen bg-white font-sans">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A1628]">
          <div className="container mx-auto px-4 sm:px-8 h-20 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img
                src="https://storage.readdy-site.link/project_files/2d2ea231-b3ca-4078-9f64-c6062285e3ca/d2c67bee-2653-4219-a221-2e9d779e273e_ChatGPT-Image-Apr-2-2026-02_21_57-PM.png?v=d5a360501a5e3b86884e2dc0f7061a82"
                alt="Technelle - AI Marketing Wisconsin"
                className="h-14"
              />
            </Link>
            <div className="hidden lg:flex items-center gap-8">
              <Link to="/services" className="text-[15px] text-white/80 hover:text-white transition-colors font-medium">Services</Link>
              <Link to="/industries" className="text-[15px] text-white/80 hover:text-white transition-colors font-medium">Industries</Link>
              <Link to="/about" className="text-[15px] text-white/80 hover:text-white transition-colors font-medium">About</Link>
              <Link to="/case-studies" className="text-[15px] text-white/80 hover:text-white transition-colors font-medium">Case Studies</Link>
              <Link to="/service-areas" className="text-[15px] text-[#2196F3] font-medium">Service Areas</Link>
            </div>
            <Link
              to="/contact"
              className="bg-[#2196F3] text-white px-6 py-3 rounded-md text-[15px] font-medium hover:bg-[#1976D2] transition-colors whitespace-nowrap"
            >
              Book Free Strategy Call
            </Link>
          </div>
          <div className="h-[2px] bg-[#2196F3]"></div>
        </nav>

        {/* Breadcrumb */}
        <div className="pt-24 pb-4 bg-white">
          <Breadcrumb />
        </div>

        {/* Hero Section */}
        <section className="relative min-h-[70vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Green%20Bay%20Wisconsin%20Fox%20River%20waterfront%20with%20Titletown%20district%20skyline%20and%20modern%20buildings%20at%20sunset%2C%20professional%20corporate%20photography%20with%20warm%20amber%20tones%20representing%20thriving%20Northeast%20Wisconsin%20business%20community%20and%20economic%20opportunity%20for%20local%20companies&width=1920&height=900&seq=greenbay-hero-003&orientation=landscape"
              alt="Green Bay Wisconsin Fox River and Titletown district — AI marketing services for Green Bay businesses"
              className="w-full h-full object-cover object-top"
              loading="eager"
              fetchPriority="high"
              decoding="async"
              width="1920"
              height="900"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
          </div>
          <div className="relative z-10 container mx-auto px-4 sm:px-8 py-20 w-full">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full mb-6">
                <i className="ri-map-pin-2-line text-[#2196F3]" aria-hidden="true"></i>
                <span className="text-white text-[14px] font-medium">Green Bay, WI · Northeast Wisconsin</span>
              </div>
              <h1 className="text-[44px] sm:text-[58px] lg:text-[68px] leading-[1.1] font-bold text-white mb-6 font-serif">
                AI Marketing for<br />
                <span className="text-[#2196F3]">Green Bay Businesses</span>
              </h1>
              <p className="text-[18px] sm:text-[20px] text-white/90 mb-8 leading-relaxed max-w-2xl">
                Green Bay is more than the Packers. It's a manufacturing powerhouse, a growing hospitality market, and a B2B hub for Northeast Wisconsin. Technelle helps Green Bay businesses cut through the noise, fill their pipelines, and build revenue that compounds — with AI systems built for how your market actually works.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="bg-[#2196F3] text-white px-8 py-4 rounded-md text-[16px] font-semibold hover:bg-[#1976D2] transition-colors whitespace-nowrap"
                >
                  Book Your Free Strategy Session
                </Link>
                <a
                  href="tel:414-530-9114"
                  className="bg-white/10 backdrop-blur-sm border border-white/30 text-white px-8 py-4 rounded-md text-[16px] font-medium hover:bg-white/20 transition-colors whitespace-nowrap"
                >
                  Call (414) 530-9114
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-[#1a1a1a] py-10">
          <div className="container mx-auto px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
              {[
                { value: '20+', label: 'Years in Wisconsin' },
                { value: '1,000+', label: 'Projects Delivered' },
                { value: '3x', label: 'Average Client ROI' },
                { value: '8+', label: 'Industries Served' },
              ].map((stat, i) => (
                <div key={i}>
                  <div className="text-[36px] font-bold text-[#2196F3] mb-1">{stat.value}</div>
                  <div className="text-[14px] text-white/70">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Green Bay Market Context */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-8">
            <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="text-[13px] uppercase tracking-widest text-[#2196F3] font-semibold mb-4">The Green Bay Market</div>
                <h2 className="text-[38px] font-serif font-bold text-[#1a1a1a] mb-6 leading-tight">
                  Green Bay Is a Different Market.<br />Your Marketing Should Be Too.
                </h2>
                <div className="space-y-5 text-[16px] text-gray-700 leading-relaxed">
                  <p>
                    Green Bay is Wisconsin's third-largest city and the economic engine of Northeast Wisconsin. The paper and packaging industry built this city, and manufacturing still drives a significant share of its GDP. But Green Bay has diversified — healthcare, hospitality, professional services, and a growing retail corridor along Oneida Street and the Titletown District have reshaped the local economy.
                  </p>
                  <p>
                    The Packers aren't just a football team here — they're a cultural identity that shapes how Green Bay businesses connect with their community. Businesses that understand this market don't just sell products and services; they become part of the fabric of Northeast Wisconsin.
                  </p>
                  <p>
                    Technelle brings 20+ years of Wisconsin market experience to Green Bay businesses. We know what resonates with Brown County buyers, how to compete in a market where word-of-mouth still matters, and how to use AI to scale what's already working.
                  </p>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Green%20Bay%20Wisconsin%20Titletown%20District%20with%20modern%20mixed-use%20development%20and%20Lambeau%20Field%20in%20background%2C%20professional%20corporate%20photography%20representing%20thriving%20Northeast%20Wisconsin%20business%20community%20and%20economic%20development%20with%20warm%20afternoon%20light&width=700&height=560&seq=greenbay-market-001&orientation=landscape"
                  alt="Green Bay Wisconsin Titletown District — representing the city's growing business economy"
                  className="w-full h-[420px] object-cover object-top"
                  loading="lazy"
                  decoding="async"
                  width="700"
                  height="420"
                />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl px-6 py-4">
                    <div className="text-[13px] text-gray-500 mb-1">Northeast Wisconsin's Hub</div>
                    <div className="text-[18px] font-bold text-[#1a1a1a]">Green Bay, Wisconsin</div>
                    <div className="text-[13px] text-[#2196F3] font-medium mt-1">Population 110,000+ · Brown County</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Industries We Serve in Green Bay */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-14">
                <div className="text-[13px] uppercase tracking-widest text-[#2196F3] font-semibold mb-3">Green Bay Industries</div>
                <h2 className="text-[40px] font-serif font-bold text-[#1a1a1a] mb-4">
                  Built for Green Bay's Economy
                </h2>
                <p className="text-[17px] text-gray-600 max-w-2xl mx-auto leading-relaxed">
                  We specialize in the industries that drive Northeast Wisconsin — not generic playbooks, but strategies built for how Green Bay businesses actually operate.
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  {
                    icon: 'ri-building-2-line',
                    title: 'Manufacturing & Paper',
                    body: 'Green Bay\'s industrial backbone. We help manufacturers generate B2B leads, build brand authority, and automate marketing workflows that free up your team for higher-value work.',
                  },
                  {
                    icon: 'ri-restaurant-line',
                    title: 'Hospitality & Food Service',
                    body: 'From Titletown restaurants to Fox River event venues, we help Green Bay hospitality businesses fill seats, drive repeat visits, and build loyal local followings with AI-powered marketing.',
                  },
                  {
                    icon: 'ri-hospital-line',
                    title: 'Healthcare & Medical',
                    body: 'Patient acquisition and retention for Green Bay clinics, dental practices, and specialty providers — compliant, targeted, and built to grow your practice sustainably.',
                  },
                  {
                    icon: 'ri-briefcase-4-line',
                    title: 'Professional Services',
                    body: 'Law firms, accounting practices, and consulting firms in the Green Bay area trust us to generate high-value client leads and build the kind of authority that wins referrals.',
                  },
                  {
                    icon: 'ri-store-2-line',
                    title: 'Retail & E-Commerce',
                    body: 'Green Bay retail businesses competing with online giants need smarter marketing. We build AI-powered systems that drive foot traffic, repeat purchases, and local loyalty.',
                  },
                  {
                    icon: 'ri-home-gear-line',
                    title: 'Trade Services & Contractors',
                    body: 'HVAC, plumbing, electrical, and home services companies in the Green Bay area — we help you dominate local search and fill your schedule with qualified service calls.',
                  },
                ].map((item, i) => (
                  <div key={i} className="bg-white rounded-xl p-7 border border-gray-100 hover:border-[#2196F3]/30 transition-all">
                    <div className="w-12 h-12 bg-[#2196F3]/10 rounded-lg flex items-center justify-center mb-5">
                      <i className={`${item.icon} text-[#2196F3] text-xl`} aria-hidden="true"></i>
                    </div>
                    <h3 className="text-[18px] font-bold text-[#1a1a1a] mb-3">{item.title}</h3>
                    <p className="text-[14px] text-gray-600 leading-relaxed">{item.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Services for Green Bay */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-14">
                <div className="text-[13px] uppercase tracking-widest text-[#2196F3] font-semibold mb-3">What We Do</div>
                <h2 className="text-[40px] font-serif font-bold text-[#1a1a1a] mb-4">
                  AI Marketing Services for Green Bay
                </h2>
                <p className="text-[17px] text-gray-600 max-w-2xl mx-auto leading-relaxed">
                  The same senior-level AI marketing expertise that drives results in Milwaukee and Madison — applied to the specific dynamics of the Green Bay market.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  {
                    icon: 'ri-search-eye-line',
                    title: 'Local SEO & AI Search Visibility',
                    body: 'Get found when Green Bay buyers search for what you offer — including the AI-generated answers that now appear at the top of Google. We build the kind of local authority that drives consistent inbound leads from Brown County and beyond.',
                    tag: 'Long-term organic growth',
                  },
                  {
                    icon: 'ri-robot-line',
                    title: 'AI Automation & Workflow Optimization',
                    body: 'Green Bay manufacturers and service businesses are leaving money on the table with manual processes. We identify where your team is spending time on tasks AI can handle — and build the systems to fix it. Most clients recover $30K–$80K in annual value.',
                    tag: 'Cost savings & scale',
                  },
                  {
                    icon: 'ri-meta-line',
                    title: 'Meta Ads & Paid Social',
                    body: 'Conversion-focused Meta campaigns built for the Green Bay market. We know which audiences respond in Northeast Wisconsin, how to position against local competitors, and how to turn ad spend into booked jobs — not just impressions.',
                    tag: 'Immediate lead flow',
                  },
                  {
                    icon: 'ri-mail-send-line',
                    title: 'Email Marketing & Lead Nurturing',
                    body: 'Most Green Bay businesses have a customer list they\'re not using. We build automated email systems that turn cold leads into paying customers and keep existing customers coming back — running on autopilot while you focus on the work.',
                    tag: 'Revenue on autopilot',
                  },
                ].map((svc, i) => (
                  <div key={i} className="bg-gray-50 rounded-2xl p-8 border border-gray-100 hover:border-[#2196F3]/30 transition-all">
                    <div className="w-12 h-12 bg-[#1a1a1a] rounded-lg flex items-center justify-center mb-5">
                      <i className={`${svc.icon} text-white text-xl`} aria-hidden="true"></i>
                    </div>
                    <h3 className="text-[22px] font-bold text-[#1a1a1a] mb-3">{svc.title}</h3>
                    <p className="text-[15px] text-gray-600 leading-relaxed mb-4">{svc.body}</p>
                    <span className="inline-block bg-[#2196F3]/10 text-[#2196F3] text-[12px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full">
                      {svc.tag}
                    </span>
                  </div>
                ))}
              </div>
              <div className="text-center mt-10">
                <Link
                  to="/services"
                  className="inline-flex items-center gap-2 text-[#2196F3] text-[15px] font-semibold hover:underline"
                >
                  See all services <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Why Green Bay Businesses Choose Technelle */}
        <section className="py-20 bg-[#0A1628]">
          <div className="container mx-auto px-4 sm:px-8">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-14">
                <div className="text-[13px] uppercase tracking-widest text-[#2196F3] font-semibold mb-3">Why Technelle</div>
                <h2 className="text-[40px] font-serif font-bold text-white mb-4">
                  A Wisconsin Agency That Knows Your Market
                </h2>
                <p className="text-[17px] text-white/75 max-w-2xl mx-auto leading-relaxed">
                  National agencies don't know that Green Bay's manufacturing sector runs on long B2B sales cycles, that the Packers season affects hospitality revenue patterns, or that Brown County buyers respond differently than Milwaukee buyers. We do.
                </p>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  {
                    icon: 'ri-map-pin-line',
                    title: 'Northeast Wisconsin Expertise',
                    body: 'We understand the Green Bay market — from the Fox River corridor to the Titletown District to the industrial parks on the city\'s west side. Local knowledge that national agencies simply can\'t replicate.',
                  },
                  {
                    icon: 'ri-user-star-line',
                    title: 'Senior-Level Only',
                    body: 'When you work with Technelle, you work directly with Melissa — a 20-year marketing operator. No junior account managers, no outsourced execution, no national playbook applied to your local market.',
                  },
                  {
                    icon: 'ri-bar-chart-grouped-line',
                    title: 'Measurable Results',
                    body: 'We define KPIs before we start and track them throughout. Our Green Bay clients see an average 3x ROI within the first year — and we can show you exactly how we get there.',
                  },
                ].map((card, i) => (
                  <div key={i} className="bg-[#0d1f35] border border-white/10 rounded-xl p-7 hover:border-[#2196F3]/50 transition-all">
                    <div className="w-12 h-12 bg-[#2196F3]/15 rounded-lg flex items-center justify-center mb-5">
                      <i className={`${card.icon} text-[#2196F3] text-xl`} aria-hidden="true"></i>
                    </div>
                    <h3 className="text-[18px] font-bold text-white mb-3">{card.title}</h3>
                    <p className="text-[14px] text-white/70 leading-relaxed">{card.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Testimonial */}
        <section className="bg-white py-16 border-t border-gray-100">
          <div className="container mx-auto px-4 sm:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <i className="ri-double-quotes-l text-gray-200 text-[80px] leading-none" aria-hidden="true"></i>
              <blockquote className="text-[26px] sm:text-[30px] font-serif text-[#1a1a1a] leading-tight mb-6 -mt-6">
                "If you're looking for a company that delivers top-notch work and values collaboration, you can stop searching. You've found it with Technelle!"
              </blockquote>
              <footer>
                <strong className="text-[#1a1a1a] text-[17px]">Tony Wojak</strong>
                <span className="text-gray-500 block text-[14px] mt-1">Owner, Tony's Heating &amp; Cooling — Milwaukee, WI</span>
              </footer>
            </div>
          </div>
        </section>

        {/* Other Service Areas */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-8">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-10">
                <div className="text-[13px] uppercase tracking-widest text-[#2196F3] font-semibold mb-3">Also Serving</div>
                <h2 className="text-[28px] font-serif font-bold text-[#1a1a1a]">Other Wisconsin Markets</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <Link to="/service-areas/milwaukee" className="bg-white rounded-xl p-6 border border-gray-100 hover:border-[#2196F3]/40 transition-all group">
                  <div className="text-[18px] font-bold text-[#1a1a1a] group-hover:text-[#2196F3] transition-colors mb-2">Milwaukee, WI</div>
                  <p className="text-[14px] text-gray-600 mb-3">Wisconsin's largest market — manufacturing, healthcare, and trade services.</p>
                  <span className="text-[13px] text-[#2196F3] font-semibold flex items-center gap-1">Milwaukee services <i className="ri-arrow-right-line"></i></span>
                </Link>
                <Link to="/service-areas/madison" className="bg-white rounded-xl p-6 border border-gray-100 hover:border-[#2196F3]/40 transition-all group">
                  <div className="text-[18px] font-bold text-[#1a1a1a] group-hover:text-[#2196F3] transition-colors mb-2">Madison, WI</div>
                  <p className="text-[14px] text-gray-600 mb-3">Wisconsin's capital — tech startups, UW-adjacent businesses, and professional services.</p>
                  <span className="text-[13px] text-[#2196F3] font-semibold flex items-center gap-1">Madison services <i className="ri-arrow-right-line"></i></span>
                </Link>
                <Link to="/service-areas" className="bg-white rounded-xl p-6 border border-gray-100 hover:border-[#2196F3]/40 transition-all group">
                  <div className="text-[18px] font-bold text-[#1a1a1a] group-hover:text-[#2196F3] transition-colors mb-2">All of Wisconsin</div>
                  <p className="text-[14px] text-gray-600 mb-3">Waukesha, Appleton, Oshkosh, Eau Claire, and every Wisconsin market statewide.</p>
                  <span className="text-[13px] text-[#2196F3] font-semibold flex items-center gap-1">All service areas <i className="ri-arrow-right-line"></i></span>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-[#0A1628]">
          <div className="container mx-auto px-4 sm:px-8 text-center">
            <h2 className="text-[40px] sm:text-[52px] font-serif font-bold text-white mb-6 leading-tight">
              Ready to Grow Your<br />
              <span className="text-[#2196F3]">Green Bay Business?</span>
            </h2>
            <p className="text-[18px] text-white/75 mb-10 max-w-2xl mx-auto leading-relaxed">
              Book a free 30-minute strategy session and walk away with a custom AI marketing roadmap for your Green Bay business — no pitch, no obligation.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-[#2196F3] text-white px-10 py-4 rounded-md text-[16px] font-semibold hover:bg-[#1976D2] transition-colors whitespace-nowrap"
              >
                Book My Free Strategy Session
              </Link>
              <a
                href="tel:414-530-9114"
                className="bg-white/10 backdrop-blur-sm border border-white/30 text-white px-10 py-4 rounded-md text-[16px] font-medium hover:bg-white/20 transition-colors whitespace-nowrap"
              >
                Call (414) 530-9114
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-[#1a1a1a] text-white py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
              <div className="lg:col-span-1">
                <img
                  src="https://storage.readdy-site.link/project_files/2d2ea231-b3ca-4078-9f64-c6062285e3ca/d2c67bee-2653-4219-a221-2e9d779e273e_ChatGPT-Image-Apr-2-2026-02_21_57-PM.png?v=d5a360501a5e3b86884e2dc0f7061a82"
                  alt="Technelle"
                  className="h-12 mb-4"
                  width="48"
                  height="48"
                  loading="lazy"
                  decoding="async"
                />
                <p className="text-[14px] text-gray-400 leading-relaxed">
                  AI-powered marketing for Green Bay and Northeast Wisconsin businesses. Based in Waukesha, serving the state and beyond.
                </p>
              </div>
              <div>
                <h5 className="text-[14px] uppercase tracking-wider text-gray-400 mb-6 font-semibold">Service Areas</h5>
                <nav className="space-y-3">
                  <Link to="/service-areas/milwaukee" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">Milwaukee, WI</Link>
                  <Link to="/service-areas/madison" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">Madison, WI</Link>
                  <Link to="/service-areas/green-bay" className="block text-[#2196F3] text-[15px] font-medium">Green Bay, WI</Link>
                  <Link to="/service-areas" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">All Service Areas</Link>
                </nav>
              </div>
              <div>
                <h5 className="text-[14px] uppercase tracking-wider text-gray-400 mb-6 font-semibold">Links</h5>
                <nav className="space-y-3">
                  <Link to="/" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">Home</Link>
                  <Link to="/about" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">About</Link>
                  <Link to="/services" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">Services</Link>
                  <Link to="/industries" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">Industries</Link>
                  <Link to="/case-studies" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">Case Studies</Link>
                  <Link to="/faq" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">FAQ</Link>
                  <Link to="/contact" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">Contact</Link>
                </nav>
              </div>
              <div>
                <h5 className="text-[14px] uppercase tracking-wider text-gray-400 mb-6 font-semibold">Contact</h5>
                <div className="space-y-3">
                  <a href="tel:4145309114" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">414-530-9114</a>
                  <a href="mailto:melissa@technelle.ai" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">melissa@technelle.ai</a>
                  <p className="text-[14px] text-gray-400">Waukesha, Wisconsin</p>
                </div>
              </div>
            </div>
            <div className="border-t border-white/10 pt-8">
              <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
                <div className="text-[14px] text-gray-400">© 2026 Technelle. All rights reserved.</div>
                <div className="flex items-center gap-6">
                  <Link to="/privacy-policy" className="text-[14px] text-gray-400 hover:text-white transition-colors">Privacy Policy</Link>
                  <Link to="/terms" className="text-[14px] text-gray-400 hover:text-white transition-colors">Terms</Link>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </Suspense>
  );
}
